import { Divider, Paper, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Theme from '../Themes/main.theme';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import GroupIcon from '@mui/icons-material/Group';
import { Box } from '@mui/system';
import HttpService from '../services/HttpService';
import { ImageItem } from '../components/Cards/Types';
import { useSnackbar } from 'notistack';
import { ISortingFilters, TagItem } from '../components/Tag/types';
import UserService from '../services/UserService';
import { Permissions } from '../lib/permissions';
import TabsLayout from '../components/Tabs/TabsLayout';
import axios, { CancelTokenSource } from 'axios';
import {
  convertTagsToArray,
  eliminateOjectNotTruthyValues,
  transformGreekToEnglish,
} from '../lib/helperFuncs';
import CardGrid from '../components/CardGrid';
import GMapsPlaceMarker from '../components/Maps/GMapsPlaceMarker';
import { useApiState } from '../hooks/useApiState';
import { ApiUsability } from '../lib/generalTypes';
import i18n from '../i18n';

const ITEMS_PER_PAGE = 8;

const UserFavorites = () => {
  const [tab, setTab] = useState(0);
  const [geoData, setGeoData] = useState<google.maps.LatLngLiteral>();
  const [data, setData] = useState<ImageItem[]>([]);
  const [professionalsData, setProfessionalsData] = useState<ImageItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [totalCountProfessionals, setTotalCountProfessionals] =
    useState<number>(0);
  const [removeFavorite, setRemoveFavorite] = useState<boolean>(false);
  const [selectedPage, setSelectedPage] = useState<number>(1);
  const [sortBy, setSortBy] = useState<string | undefined>('1');
  const [selectedTags, setSelectedTags] = useState<TagItem[]>([]);

  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const { apiEnabled, loadingApi } = useApiState(
    ApiUsability.Google_Maps,
    true
  );

  document.title = t('Αγαπημένα');

  const resetFilters = () => {
    setSortBy('1');
    setSelectedPage(1);
    setSelectedTags([]);
  };

  const sortingItems: ISortingFilters[] | undefined =
    tab === 0
      ? [
        {
          id: '1',
          refValue: 'title',
          sortOrder: 'asc',
          label: t('Αλφαβητικά'),
        },
        {
          id: '2',
          refValue: 'locations:locationsCategory',
          sortOrder: 'asc',
          label: t('Κατηγορία'),
        },
        {
          id: '3',
          refValue: 'created',
          sortOrder: 'desc',
          label: t('Νεότερης Καταχώρισης'),
        },
        {
          id: '4',
          refValue: 'locations:region',
          sortOrder: 'asc',
          label: t('Περιφερειακή Ενότητα'),
        },
      ]
      : [
        {
          id: '1',
          refValue: 'professionals:businessName',
          sortOrder: 'asc',
          label: t('Αλφαβητικά'),
        },
        {
          id: '2',
          refValue: 'professionals:servicesCategory',
          sortOrder: 'asc',
          label: t('Κατηγορία'),
        },
        {
          id: '3',
          refValue: 'created',
          sortOrder: 'desc',
          label: t('Νεότερης Καταχώρισης'),
        },
        {
          id: '4',
          refValue: 'professionals:professionalRegion',
          sortOrder: 'asc',
          label: t('Περιφερειακή Ενότητα'),
        },
      ];

  const onSubmitFavoriteProfessional = async (
    id: number,
    isFavorite: boolean
  ) => {
    if (!(await UserService.isAuthenticated())) {
      return;
    }
    try {
      const resp = await HttpService.getAxiosClient().patch(
        `${process.env.REACT_APP_BACKEND_URL}/professionals/favorites/${id}`,
        { is_favorite: isFavorite }
      );
      if (resp.status === 200) {
        setRemoveFavorite(!removeFavorite);
        enqueueSnackbar(
          `${t('O επαγγελματίας')} ${i18n.language === 'en'
            ? transformGreekToEnglish(`${resp.data.title}`)
            : resp.data.title
          } ${t('αφαιρέθηκε από τα αγαπημένα σας')}`,
          { variant: 'info' }
        );
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        t('Παρουσιάστηκε σφάλμα, παρακαλούμε προσπαθήστε αργότερα.'),
        { variant: 'error' }
      );
    }
  };

  const onSubmitFavoriteLocation = async (id: number, isFavorite: boolean) => {
    if (
      !UserService.hasRole([
        Permissions.PermissionExternalLocationFavoritesUpdateMy,
      ])
    ) {
      return;
    }
    try {
      const resp = await HttpService.getAxiosClient().patch(
        `${process.env.REACT_APP_BACKEND_URL}/locations/favorites/${id}`,
        { is_favorite: isFavorite }
      );
      if (resp.status === 200) {
        setRemoveFavorite(!removeFavorite);
        enqueueSnackbar(
          `${t('Η τοποθεσία')} ${i18n.language === 'en'
            ? transformGreekToEnglish(`${resp.data.title}`)
            : resp.data.title
          } ${t('αφαιρέθηκε από τα αγαπημένα σας')}`,
          { variant: 'info' }
        );
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        t('Παρουσιάστηκε σφάλμα, παρακαλούμε προσπαθήστε αργότερα.'),
        { variant: 'error' }
      );
    }
  };

  const handleSortingChange = (sortingItemId?: string) => {
    setSortBy(sortingItemId);
  };

  const handleOnPageChange = (page: number) => {
    setSelectedPage(page);
  };

  const handleTagChange = (selectedTagIds: any) => {
    setSelectedTags([...selectedTagIds]);
  };

  const getFavoriteLocations = async (source: CancelTokenSource) => {
    setIsLoading(true);
    const sort_by_entity = sortingItems.find((item) => item.id === sortBy);
    try {
      const filters: any = {
        per_page: ITEMS_PER_PAGE,
        sort_by: sort_by_entity?.refValue,
        sort_order: 'asc',
        page: selectedPage,
        tags: selectedTags.map((tag) => tag.key),
      };
      const resp = await HttpService.getAxiosClient().get(
        `${process.env.REACT_APP_BACKEND_URL}/locations/favourites`,
        {
          params: eliminateOjectNotTruthyValues(filters),
          cancelToken: source.token,
        }
      );
      const results: ImageItem[] = resp.data.omekaResp.map((x: any) => {
        return {
          id: x['o:id'],
          title: x['o:title'],
          img: x.thumbnail_display_urls.large,
          subtitle: x?.['locations:region']?.[0]?.['@value'],
          tags: convertTagsToArray(
            x?.['locations:locationsKeyWords']?.[0]?.['@value']
          ),
          favorite: true,
          redirect: 'location/details',
          alwayShowBanner: true,
          displayFavorite: true,
          enableFavoriteButton: UserService.hasRole([
            Permissions.PermissionExternalLocationFavoritesUpdateMy,
          ]),
        };
      });
      const mapData: google.maps.LatLngLiteral = UserService.hasRole([
        Permissions.PermissionExternalLocationFavoritesUpdateMy,
      ])
        ? resp.data.omekaResp.map((x: any) => {
          return {
            id: x['o:id'],
            title: x['o:title'],
            redirect: 'location/details',
            img: x.thumbnail_display_urls.square,
            lat: Number(x?.['locations:latitude']?.[0]?.['@value']),
            lng: Number(x?.['locations:longitude']?.[0]?.['@value']),
          };
        })
        : [];
      setGeoData(mapData);
      setData(results);
      setTotalCount(+resp.data.total_count);
      setIsLoading(false);
    } catch (error: any) {
      if (error.response?.data.statusCode === 403) {
        // inform user has no permission not only the generic error handling
        enqueueSnackbar(t('Λυπούμαστε δεν έχετε Άδεια.'), { variant: 'error' });
        setData([]);
        setIsLoading(false);
      }
      if (axios.isCancel(error)) {
        console.info('successfully aborted');
      } else {
        console.error(error);
      }
    }
  };

  const getFavoriteProfessionals = async (source: CancelTokenSource) => {
    const sort_by_entity = sortingItems.find((item) => item.id === sortBy);
    const filters: any = {
      per_page: ITEMS_PER_PAGE,
      sort_by: sort_by_entity?.refValue,
      sort_order: 'asc',
      page: selectedPage,
      tags: selectedTags.map((tag) => tag.key),
    };
    setIsLoading(true);
    try {
      const resp = await HttpService.getAxiosClient().get(
        `${process.env.REACT_APP_BACKEND_URL}/professionals/favourites`,
        {
          params: eliminateOjectNotTruthyValues(filters),
          cancelToken: source.token,
        }
      );
      const results: ImageItem[] = resp.data.omekaResp.map((x: any) => {
        return {
          id: x['o:id'],
          title: x['o:title'],
          img: x.thumbnail_display_urls.large,
          subtitle: x?.['professionals:professionalRegion']?.[0]?.['@value'],
          alwayShowBanner: true,
          favorite: true,
          redirect: 'professionals/details',
          displayFavorite: true,
        };
      });
      const mapData: google.maps.LatLngLiteral = resp.data.omekaResp.map(
        (x: any) => {
          return {
            id: x['o:id'],
            title: x['o:title'],
            img: x.thumbnail_display_urls.square,
            redirect: 'professionals/details',
            lat: Number(x?.['locations:latitude']?.[0]?.['@value']),
            lng: Number(x?.['locations:longitude']?.[0]?.['@value']),
          };
        }
      );
      setGeoData(mapData);
      setProfessionalsData(results);
      setTotalCountProfessionals(+resp.data.total_count);
      setIsLoading(false);
    } catch (error: any) {
      if (error.response?.data.statusCode === 403) {
        // inform user has no permission not only the generic error handling
        enqueueSnackbar(t('Λυπούμαστε δεν έχετε Άδεια.'), { variant: 'error' });
        setProfessionalsData([]);
        setIsLoading(false);
      }
      if (axios.isCancel(error)) {
        console.info('successfully aborted');
      } else {
        console.error(error);
      }
    }
  };

  useEffect(() => {
    const CancelToken = axios.CancelToken;
    const source = CancelToken.source();

    if (tab === 0) {
      UserService.hasRole([
        Permissions.PermissionExternalLocationFavoritesReadMy,
      ])
        ? getFavoriteLocations(source)
        : getFavoriteProfessionals(source);
    } else {
      getFavoriteProfessionals(source);
    }

    return () => {
      source.cancel();
    };
  }, [removeFavorite, tab, selectedTags, sortBy, selectedPage]);

  const tabLabels = [
    ...(UserService.hasRole([
      Permissions.PermissionExternalLocationFavoritesReadMy,
    ])
      ? [
        {
          label: <Typography variant='h4'>{t('Τοποθεσίες')}</Typography>,
          icon: <LocationOnIcon />,
        },
      ]
      : []),
    ...(UserService.hasRole([
      Permissions.PermissionExternalProfessionalsFavoritesReadMy,
    ])
      ? [
        {
          label: <Typography variant='h4'>{t('Επαγγελματίες')}</Typography>,
          icon: <GroupIcon />,
        },
      ]
      : []),
  ];

  const tab1 = (
    <>
      <Box mt={6} display='flex'
        justifyContent='space-between'>
        <Typography
          color={Theme?.labelColor?.main}
          variant='h3'
          ml={3}
          sx={{ alignSelf: 'center' }}
        >
          {totalCount} {t('Αγαπημένες Τοποθεσίες')}
        </Typography>
      </Box>
      <CardGrid
        grid={{
          gridData: data,
          onTagClick: handleTagChange,
          onSubmitFavorite: onSubmitFavoriteLocation,
          selectedTags: selectedTags,
        }}
        isLoading={isLoading}
        pagination={{
          serverSidePagination: false,
          itemsPerPage: ITEMS_PER_PAGE,
          totalResults: totalCount,
          selectedPage: selectedPage,
          handleOnPageChange: handleOnPageChange,
        }}
        sorting={{
          clientSideSorting: false,
          handleSortingChange: handleSortingChange,
          defaultValue: sortBy,
          sortingItems: sortingItems,
        }}
      />
    </>
  );
  const tab2 = (
    <>
      <Box mt={6} display='flex'
        justifyContent='space-between'>
        <Typography
          color={Theme?.labelColor?.main}
          variant='h3'
          ml={3}
          sx={{ alignSelf: 'center' }}
        >
          {totalCountProfessionals} {t('Αγαπημένοι Επαγγελματίες')}
        </Typography>
      </Box>
      <CardGrid
        grid={{
          gridData: professionalsData,
          onSubmitFavorite: onSubmitFavoriteProfessional,
        }}
        isLoading={isLoading}
        pagination={{
          serverSidePagination: false,
          itemsPerPage: ITEMS_PER_PAGE,
          totalResults: totalCountProfessionals,
          selectedPage: selectedPage,
          handleOnPageChange: handleOnPageChange,
        }}
        sorting={{
          clientSideSorting: false,
          handleSortingChange: handleSortingChange,
          defaultValue: sortBy,
          sortingItems: sortingItems,
        }}
      />
    </>
  );
  const tabContent = [
    ...(UserService.hasRole([
      Permissions.PermissionExternalLocationFavoritesReadMy,
    ])
      ? [tab1]
      : []),
    ...(UserService.hasRole([
      Permissions.PermissionExternalProfessionalsFavoritesReadMy,
    ])
      ? [tab2]
      : []),
  ];

  return (
    <>
      {/* <Box mt={2} mb={2}>
        <BackButton onClick={() => navigate(-1)}/>
      </Box>       */}
      <Typography sx={{ my: 2 }} variant='h2'
        color={Theme?.labelColor?.main}>
        {t('Αγαπημένα')}
      </Typography>
      <Divider sx={{ mb: 5, border: 1, color: Theme?.dividerColor?.main }} />
      <TabsLayout
        tab={tab}
        setTab={setTab}
        tabLabels={tabLabels}
        tabContent={tabContent}
        resetFilters={resetFilters}
      />
      {apiEnabled && !loadingApi
        ? tab === 0 &&
        geoData && (
          <Box mt={2}>
            <Paper
              variant='outlined'
              sx={{
                height: 'fit-content',
                borderRadius: 4,
                boxShadow: 0,
                backgroundColor: 'transparent',
              }}
            >
              <Box m={2}>
                <GMapsPlaceMarker
                  height='600px'
                  zoom={7}
                  location={geoData}
                />
              </Box>
            </Paper>
          </Box>
        )
        : null}
    </>
  );
};

export default UserFavorites;
